import type { KanbanComment, KanbanCommentAgent } from '@entities/kanban-comment'

import { useKanbanCommentsStore } from '@entities/kanban-comment'
import { useProfileStore } from '@entities/viewer'
import { useCommonStore } from '@shared/model'
import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface SaveCommentResponse {
  id: number
  comment: string
  created_at: string
  updated_at: string
}

interface SaveCommentApiOptions {
  taskId: string | number
  comment: string
  onStart?: () => void
  onSuccess?: (data: KanbanComment) => void
  onError?: (err: unknown) => void
  onFinish?: () => void
}

export const useSaveTasksKanbanCommentApi = async (options: SaveCommentApiOptions): Promise<KanbanComment | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.kanban-tasks.comments', { task_id: options.taskId })

  const kanbanCommentsStore = useKanbanCommentsStore()
  const commonStore = useCommonStore()
  const profileStore = useProfileStore()

  options.onStart?.()
  commonStore.setLoading(true)

  try {
    const { data } = await $axios.post(url!, { comment: options.comment })
    const response: SaveCommentResponse = data.data

    const agent: KanbanCommentAgent | null = profileStore.user
      ? { id: profileStore.user.id, name: profileStore.user.name }
      : null

    const comment: KanbanComment = { ...response, agent }

    kanbanCommentsStore.addComment(comment)
    commonStore.setLoading(false)

    options.onSuccess?.(comment)

    return comment
  } catch (err: unknown) {
    console.error(err)
    toast.error(getAxiosErrorMessage(err))
    commonStore.setLoading(false)
    options.onError?.(err)

    return null
  } finally {
    options.onFinish?.()
  }
}